import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DataTableColumn, DataTableRow } from "../../components/carbon/DataTable/DataTable";
import { MaterialResponse, MaterialWithSuppliersResponse } from "../../types";
import { getOwnerName } from "./materialRowHelpers";

/** The `t` returned by `useTranslation()` — typed loosely (rather than importing i18next's own
 * generic `TFunction`) since every call site here only ever calls it with a plain key. */
type TFunc = ReturnType<typeof useTranslation>["t"];

/** Accent colours for the row's status line (see `DataTable`'s `rowAccent`) — the same green/grey
 * active/inactive semantics the old status `Tag` carried, now drawn as a bar at the row's leading
 * edge instead of its own column. Carbon support-success / border-strong tokens. */
export const STATUS_ACCENT_ACTIVE = "var(--cds-support-success, #24a148)";
export const STATUS_ACCENT_INACTIVE = "var(--cds-border-strong, #8d8d8d)";

/**
 * One row shape shared by both list mode (`MaterialWithSuppliersResponse` — nested `material`
 * plus the three supplier/order counters) and search mode (flat `MaterialResponse`, no
 * counters — see `hooks/useMaterials.ts`'s own comments on why those two endpoints return
 * different shapes). `owner` and the three counters are optional because search rows never
 * populate them and the plain-list-mode `/materials` screen never populates `owner`.
 *
 * `active` is NOT a rendered column — it drives the row's leading status line via `DataTable`'s
 * `rowAccent` (green = active, grey = inactive), replacing the old status `Tag` column. Delivery
 * format and site columns were removed from this table entirely.
 */
export interface MaterialRow extends DataTableRow {
  id: string;
  internalCode: string;
  name: ReactNode;
  active: boolean;
  owner?: string;
  validatedSuppliers?: number;
  suppliers?: number;
  orders?: number;
}

const nameLink = (id: string, name: string): ReactNode => (
  <Link to={`/materials/${id}`}>{name}</Link>
);

/** List-mode row: `MaterialWithSuppliersResponse` (nested `material` + counters). `showOwner`
 * adds the Owner/Responsible column the `/materials/company` screen shows (its old
 * `allPICompanyMaterialsTh`); the plain `/materials` list shows no identity column now that the
 * delivery Site column was removed. */
export const mapListItemToRow = (
  item: MaterialWithSuppliersResponse,
  t: TFunc,
  showOwner: boolean
): MaterialRow => {
  const { material, suppliersQuantity, supplierValidatedQuantity, activeOrdersQuantity } = item;
  return {
    id: material.id,
    internalCode: material.ean,
    name: nameLink(material.id, material.globalMaterialName),
    active: material.active,
    ...(showOwner ? { owner: getOwnerName(material.owner) } : {}),
    validatedSuppliers: supplierValidatedQuantity,
    suppliers: suppliersQuantity,
    orders: activeOrdersQuantity,
  };
};

/** Search-mode row: flat `MaterialResponse`, no counters — same shape on both `/materials`
 * (`useSearchMaterials`) and `/materials/company` (`useSearchAllMaterials`), so one mapper
 * covers both screens' search results. */
export const mapSearchItemToRow = (material: MaterialResponse): MaterialRow => ({
  id: material.id,
  internalCode: material.ean,
  name: nameLink(material.id, material.globalMaterialName),
  active: material.active,
});

/** The row's status accent colour — green when active, grey when not. Passed to `DataTable` as
 * `rowAccent`; shared by list and search modes since both carry `active`. */
export const materialRowAccent = (row: MaterialRow): string =>
  row.active ? STATUS_ACCENT_ACTIVE : STATUS_ACCENT_INACTIVE;

export const listHeaders = (
  t: TFunc,
  showOwner: boolean
): DataTableColumn<MaterialRow>[] => [
  { key: "internalCode", header: t("tables.internalCode") },
  { key: "name", header: t("tables.name") },
  ...(showOwner
    ? [{ key: "owner" as const, header: t("tables.owner") }]
    : []),
  { key: "validatedSuppliers", header: t("tables.validatedSuppliers") },
  { key: "suppliers", header: t("tables.suppliers") },
  { key: "orders", header: t("tables.orders") },
];

export const searchHeaders = (t: TFunc): DataTableColumn<MaterialRow>[] => [
  { key: "internalCode", header: t("tables.internalCode") },
  { key: "name", header: t("tables.name") },
];
